import { useEffect, useState } from "react";
import { api } from "../lib/api";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Checkbox } from "../components/ui/checkbox";
import { toast } from "sonner";
import { Save } from "lucide-react";

export default function EmergencyPlan() {
  const [plan, setPlan] = useState(null);

  useEffect(() => {
    api.get("/action-plan").then((r) => setPlan(r.data));
  }, []);

  if (!plan) return <div className="py-12 text-center text-slate-500 text-sm">Loading...</div>;

  const updateSection = (idx, items) => {
    const copy = JSON.parse(JSON.stringify(plan));
    copy.sections[idx].items = items;
    setPlan(copy);
  };
  const toggleItem = (si, ii) => {
    const copy = JSON.parse(JSON.stringify(plan));
    copy.sections[si].items[ii].done = !copy.sections[si].items[ii].done;
    setPlan(copy);
  };
  const addItem = (si, text) => {
    if (!text.trim()) return;
    const copy = JSON.parse(JSON.stringify(plan));
    copy.sections[si].items.push({ text, done: false });
    setPlan(copy);
  };
  const removeItem = (si, ii) => {
    const copy = JSON.parse(JSON.stringify(plan));
    copy.sections[si].items.splice(ii, 1);
    setPlan(copy);
  };

  const save = async () => {
    await api.put("/action-plan", plan);
    toast.success("Plan saved");
  };

  return (
    <div className="space-y-6" data-testid="emergency-plan-page">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="label-overline">If something happens to me</div>
          <h1 className="font-heading text-3xl sm:text-4xl font-semibold tracking-tight text-slate-900 mt-1">
            Emergency Action Plan
          </h1>
          <p className="text-sm text-slate-600 mt-2 max-w-2xl">
            A clear, human checklist for your family — phase by phase — so nothing is missed.
          </p>
        </div>
        <Button onClick={save} data-testid="plan-save-button">
          <Save className="h-4 w-4 mr-2" /> Save plan
        </Button>
      </div>

      <Card className="p-6 bg-slate-900 text-white">
        <div className="label-overline text-slate-300">Personal Message</div>
        <Textarea
          rows={4}
          value={plan.family_message || ""}
          onChange={(e) => setPlan({ ...plan, family_message: e.target.value })}
          className="mt-3 bg-slate-800 border-slate-700 text-white placeholder:text-slate-400"
          data-testid="plan-family-message"
        />
      </Card>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card className="p-5">
          <Label>Will Location</Label>
          <Input className="mt-2" value={plan.will_location || ""}
            onChange={(e) => setPlan({ ...plan, will_location: e.target.value })}
            data-testid="plan-will-location" />
        </Card>
        <Card className="p-5">
          <Label>Locker Key Location</Label>
          <Input className="mt-2" value={plan.locker_key_location || ""}
            onChange={(e) => setPlan({ ...plan, locker_key_location: e.target.value })}
            data-testid="plan-locker-location" />
        </Card>
        <Card className="p-5">
          <Label>Monthly Household Need (₹)</Label>
          <Input type="number" className="mt-2" value={plan.monthly_expense_estimate || ""}
            onChange={(e) => setPlan({ ...plan, monthly_expense_estimate: Number(e.target.value) })}
            data-testid="plan-monthly-need" />
        </Card>
      </div>

      {plan.sections.map((sec, si) => (
        <Card key={sec.key} className="p-6" data-testid={`plan-section-${sec.key}`}>
          <div className="flex items-center justify-between mb-4">
            <div>
              <div className="label-overline">Phase</div>
              <div className="font-heading text-xl font-semibold mt-1">{sec.title}</div>
            </div>
            <div className="text-xs text-slate-500 tabular-nums">
              {sec.items.filter((i) => i.done).length} / {sec.items.length} done
            </div>
          </div>
          <ul className="space-y-3">
            {sec.items.map((item, ii) => (
              <li key={ii} className="flex items-start gap-3 group">
                <Checkbox
                  checked={item.done}
                  onCheckedChange={() => toggleItem(si, ii)}
                  className="mt-1"
                  data-testid={`plan-item-check-${sec.key}-${ii}`}
                />
                <div className="flex-1 min-w-0">
                  <Input
                    value={item.text}
                    onChange={(e) => {
                      const items = [...sec.items];
                      items[ii] = { ...items[ii], text: e.target.value };
                      updateSection(si, items);
                    }}
                    className={`border-transparent focus:border-slate-300 px-2 ${item.done ? "line-through text-slate-400" : ""}`}
                  />
                </div>
                <button
                  onClick={() => removeItem(si, ii)}
                  className="opacity-0 group-hover:opacity-100 text-xs text-red-600 hover:underline"
                  data-testid={`plan-item-remove-${sec.key}-${ii}`}
                >
                  remove
                </button>
              </li>
            ))}
          </ul>
          <AddItem onAdd={(t) => addItem(si, t)} section={sec.key} />
        </Card>
      ))}
    </div>
  );
}

function AddItem({ onAdd, section }) {
  const [text, setText] = useState("");
  return (
    <div className="flex gap-2 mt-4">
      <Input
        value={text}
        onChange={(e) => setText(e.target.value)}
        placeholder="Add step..."
        onKeyDown={(e) => e.key === "Enter" && (onAdd(text), setText(""))}
        data-testid={`plan-item-input-${section}`}
      />
      <Button variant="outline" onClick={() => { onAdd(text); setText(""); }}
        data-testid={`plan-item-add-${section}`}>
        Add
      </Button>
    </div>
  );
}
